import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import type { Location } from 'react-router-dom';
import { useAuth, type NexoraUser } from '../context/AuthContext';
import api from '../api/axios';

const ROLE_MAP: Record<string, NexoraUser['role']> = {
  superadmin:    'SUPER_ADMIN',
  super_admin:   'SUPER_ADMIN',
  society_admin: 'SOCIETY_ADMIN',
  societyadmin:  'SOCIETY_ADMIN',
  admin:         'SOCIETY_ADMIN',
  staff:         'STAFF',
  viewer:        'VIEWER',
  resident:      'RESIDENT',
  tenant:        'TENANT',
};

function normalizeRole(raw: string | null | undefined): NexoraUser['role'] {
  if (!raw) return undefined as unknown as NexoraUser['role'];
  return ROLE_MAP[raw.toLowerCase()] ?? (raw as NexoraUser['role']);
}

// Phone number + WhatsApp OTP — an alternative sign-in path alongside (not
// replacing) email/username+password. Available to residents/tenants and to
// Society Admin/Staff/Super Admin accounts with a phone number on file.
export function usePhoneLogin() {
  const [requesting, setRequesting] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { login, updateUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const requestOtp = async (phone: string) => {
    setError(null);
    setRequesting(true);
    try {
      await api.post('/api/v1/auth/otp/request', { phone });
      setOtpSent(true);
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setRequesting(false);
    }
  };

  const verifyOtp = async (phone: string, otp: string) => {
    setError(null);
    setVerifying(true);
    try {
      const res = await api.post('/api/v1/auth/otp/verify', { phone, otp });
      const d: Record<string, unknown> = res.data?.data ?? res.data;

      const user: NexoraUser = {
        id: (d.userId ?? d.id) as string,
        fullName: d.fullName as string,
        email: d.email as string,
        role: normalizeRole(d.role as string),
        societyId: (d.societyId as string) ?? null,
        societyName: null,
        lastLoginAt: null,
      };

      login(d.accessToken as string, d.refreshToken as string, user);

      // The otp/verify response is a TokenResponse, not the full profile — fields
      // like phone and societyName only come from /me. Backfill in the background.
      api.get('/api/v1/auth/me').then((meRes) => {
        const md: Record<string, unknown> = meRes.data?.data ?? meRes.data;
        updateUser({
          ...user,
          phone: (md.phone as string) ?? null,
          societyName: (md.societyName as string) ?? null,
          societyCode: (md.societyCode as string) ?? null,
        });
      }).catch(() => {});

      // SUPER_ADMIN always lands on /admin — never follow state.from which may
      // be a society-scoped route they have no access to.
      if (user.role === 'SUPER_ADMIN') {
        navigate('/admin', { replace: true });
      } else {
        const from = (location.state as { from?: Location })?.from;
        navigate(from?.pathname ?? '/dashboard', { replace: true });
      }
    } catch (err: unknown) {
      const e = err as { response?: { status?: number; data?: { message?: string; detail?: string } } };
      setError(e.response?.data?.message ?? e.response?.data?.detail ?? 'Invalid or expired code. Please try again.');
    } finally {
      setVerifying(false);
    }
  };

  const reset = () => {
    setOtpSent(false);
    setError(null);
  };

  return { requestOtp, verifyOtp, reset, requesting, verifying, otpSent, error };
}
